import baseApi from './base';

/**
 * Per-artefact verdicts (LMS2-5096 deterministic tier + LMS2-5097 topic scope).
 * `out_of_scope` is the AI topic-scope verdict (Layer 2); there is no `vague` — an
 * unclear prompt fails topic scope. LMS2-5098 widens the file union with
 * `not_relevant`; LMS2-5141 with `too_complex` (images that need more memory to render
 * than the worker has).
 */
export type PromptCheckStatus = 'ok' | 'too_short' | 'blocked' | 'out_of_scope';
export type TargetLength = 'short' | 'medium' | 'long';
export type FileCheckStatus =
  | 'ok'
  | 'blocked'
  | 'unreadable'
  | 'too_long'
  | 'too_much_text'
  | 'too_complex'
  | 'not_relevant';

export interface SubmitFileResult {
  /** Context-file slot (0|1) — the join key back onto the file list that was sent. */
  slot: number;
  /** Filename as the API received it (cobra prefixes its temp-file name). Informational. */
  name: string;
  status: FileCheckStatus;
  /** LMS2-5098: the admin overrode a `not_relevant` verdict; the file is usable. */
  accepted?: boolean;
}

/** One staged context file, as returned by the CKFinder upload. */
export interface StagedContextFile {
  slot: number;
  s3Key: string;
  name: string;
  /** LMS2-5098 \"use it anyway\": Create clicked again with this file still flagged. */
  accepted?: boolean;
}

export interface SubmitAiCreateRequest {
  /** The draft this click continues. Omitted on the first click of a modal session. */
  requestId?: string;
  name: string;
  prompt: string;
  targetLength: TargetLength;
  /**
   * Every live context file, every click, as a reference to the object already staged in
   * S3. References, not bytes: a second click after a prompt typo costs a few hundred
   * bytes instead of re-posting up to 60MB through cobra.
   */
  files: StagedContextFile[];
}

/**
 * The single Create-with-AI submit contract (LMS2-5164). One click checks the whole
 * form and, when everything passes, generation is already queued by the time this
 * resolves: `requestId` is what the progress modal polls.
 *
 * The answer is in `outcome`, never in the status code. cobra transparently retries
 * some codes on POST, so a rejection sent as 4xx would be re-submitted behind the
 * admin's back. HTTP errors mean something that is not a verdict: 403 quota, 503 a
 * checker was unavailable, 400 a staged file was rejected before it was read. The
 * concurrency limit is not one of them; it arrives as a rejected `outcome` carrying
 * `blockedBy`, so the per-field verdicts in the same response still reach the admin.
 */
/**
 * Error bodies from this endpoint carry the draft's id whenever one was created before
 * the failure, so a retry can reconcile against it instead of starting a new draft.
 */
export interface SubmitAiCreateErrorBody {
  error?: string;
  message?: string;
  requestId?: string;
}

export interface SubmitAiCreateResponse {
  outcome: 'started' | 'rejected';
  requestId: string;
  /**
   * Set on a `rejected` that no per-field message explains. 'concurrency' means the
   * tenant is already running the maximum number of generations. The prompt and file
   * verdicts in the same response still stand.
   */
  blockedBy?: 'concurrency';
  prompt?: { status: PromptCheckStatus };
  files: SubmitFileResult[];
}

/**
 * Generation status contract (LMS2-5101). The FE synthesizes the progress steps from
 * these coarse signals, exactly like the PPT import flow.
 *
 * `draft` (LMS2-5141): a file failed a check only the worker can make (a Word file that
 * renders past the page limit). The request is back to the draft the form was editing,
 * with `files` saying which file — the form shows it exactly like a submit-time verdict.
 */
export type AiGenerationStatus =
  | 'draft'
  | 'queued'
  | 'generating'
  | 'completed'
  | 'failed';

export interface AiGenerationStatusResponse {
  status: AiGenerationStatus;
  slidesDone: number;
  slidesPlanned: number;
  /** Non-null iff status === 'completed' — where the FE navigates on success. */
  elearningId: string | null;
  error: string | null;
  /** 'INTERNAL' is the only failure a retry can fix; the rest fail the same way again. */
  errorCode: string | null;
  /** Only with status 'draft': every live file's verdict, joined on slot like a submit result. */
  files?: SubmitFileResult[];
}

const aiCreateApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    submitAiCreate: build.mutation<
      SubmitAiCreateResponse,
      SubmitAiCreateRequest
    >({
      // Plain JSON now that the files are references, so this goes back through the
      // shared base query instead of the FormData one. That restores the 401 handling:
      // an expired session redirects to SSO again rather than failing opaquely.
      query: ({ requestId, name, prompt, targetLength, files }) => ({
        url: 'ai-content/generation-requests',
        method: 'POST',
        body: {
          ...(requestId ? { requestId } : {}),
          name,
          prompt,
          targetLength,
          files,
        },
      }),
    }),

    /**
     * Restarts the request the modal is already polling, rather than resubmitting the
     * form: nothing is re-uploaded or re-screened. 409 means it has moved on.
     */
    retryAiGeneration: build.mutation<{ status: 'queued' }, string>({
      query: (requestId) => ({
        url: `ai-content/generation-requests/${requestId}/retry`,
        method: 'POST',
      }),
    }),

    /**
     * Polls the generation-request status while AiGeneratingModal is open.
     *
     * Untagged on purpose: it must stay isolated from the main elearning cache so
     * mutations that invalidate ElearningTag don't reset the polling interval
     * (same rationale as getImportStatus in pptImport.ts).
     *
     * Callers pass { pollingInterval } at the call site (5s, matching the PPT flow).
     */
    getAiGenerationStatus: build.query<AiGenerationStatusResponse, string>({
      query: (requestId) => `ai-content/generation-requests/${requestId}`,
    }),
  }),
});

export const {
  useSubmitAiCreateMutation,
  useRetryAiGenerationMutation,
  useGetAiGenerationStatusQuery,
} = aiCreateApi;

export default aiCreateApi;
